import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Phone } from 'lucide-react';
import { validateIndianMobile } from '../utils/phoneValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = ({ variant = 'light', source = 'form', projectName = 'Nova Homes', buttonText = 'Schedule a Visit' }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    phone: ''
  });
  const [phoneError, setPhoneError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'phone' && phoneError) setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { valid, normalized, error } = validateIndianMobile(formData.phone);
    if (!valid) {
      setPhoneError(error || 'Please enter a valid mobile number.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        first_name: formData.first_name,
        last_name: '',
        email: '',
        phone: normalized,
        message: `Lead from ${source}`,
        property_interest: projectName
      });
      // Meta Pixel — Lead event (fires on successful inquiry submit)
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: projectName, source });
      }
      toast.success('Thank you. We\'ll respond shortly.');
      setFormData({ first_name: '', phone: '' });
      setPhoneError('');
    } catch (error) {
      console.error('Error:', error);
      const status = error?.response?.status;
      const apiMsg = error?.response?.data?.detail;
      if (status === 429) {
        toast.error(apiMsg || 'Too many submissions. Please try again later.');
      } else if (status === 400 && apiMsg) {
        setPhoneError(apiMsg);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="lead-form" className="space-y-4">
      <input
        type="text"
        name="first_name"
        value={formData.first_name}
        onChange={handleChange}
        placeholder="Your Name"
        required
        data-testid="lead-name"
        className="w-full bg-[#F7F7F5] border border-[#E5E5E3] px-4 py-3.5 text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#111111]/30 transition-colors"
      />
      <input
        type="tel"
        name="phone"
        value={formData.phone}
        onChange={handleChange}
        placeholder="Phone Number"
        required
        maxLength={15}
        data-testid="lead-phone"
        className={`w-full bg-[#F7F7F5] border px-4 py-3.5 text-[#111111] placeholder:text-[#999999] focus:outline-none transition-colors ${
          phoneError ? 'border-red-400 focus:border-red-500' : 'border-[#E5E5E3] focus:border-[#111111]/30'
        }`}
      />
      {phoneError && (
        <p data-testid="lead-phone-error" className="text-red-500 text-xs font-light -mt-2 pl-1">
          {phoneError}
        </p>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="lead-submit"
        className="w-full bg-[#111111] hover:bg-[#222222] text-white py-3.5 font-medium tracking-wide transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {isSubmitting ? (
          <Loader2 size={18} className="animate-spin" />
        ) : null}
        {isSubmitting ? 'Sending...' : buttonText}
      </button>
    </form>
  );
};

export default LeadForm;
